import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

const STATUS_META = {
  unread: { label: "New", color: "#ef4444", bg: "#fef2f2", border: "#fca5a5" },
  read: { label: "Read", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  resolved: {
    label: "Resolved",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
  },
};

const SUBJECT_EMOJI = {
  "General Enquiry": "💬",
  "Order Issue": "📦",
  "Product Feedback": "⭐",
  "Bulk / Catering Order": "🍱",
  "Partnership / Collaboration": "🤝",
  Complaint: "⚠️",
  Other: "📋",
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return (
    dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  );
};

export default function AdminContactQueries() {
  const navigate = useNavigate();

  const [queries, setQueries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | read | resolved
  const [expanded, setExpanded] = useState(null); // expanded query _id
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filter !== "all") params.set("status", filter);
      const res = await api.get(`/contact/queries?${params}`);
      setQueries(res.data.queries || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      toast.error("Failed to load queries");
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/contact/queries/${id}`, { status });
      setQueries((prev) =>
        prev.map((q) => (q._id === id ? { ...q, status } : q)),
      );
      if (status === "read" || status === "resolved") {
        setUnreadCount((n) => Math.max(0, n - 1));
      }
      toast.success(`Marked as ${STATUS_META[status].label}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  const deleteQuery = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/contact/queries/${id}`);
      setQueries((prev) => prev.filter((q) => q._id !== id));
      setTotal((n) => n - 1);
      toast.success("Query deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const unreadQueries = queries.filter((q) => q.status === "unread");
  const otherQueries = queries.filter((q) => q.status !== "unread");

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <AdminNavbar />
      <div className="max-w-screen-xl mx-auto p-4 md:p-8">
        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="border border-green-500/30 text-green-600 rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-green-500/10 bg-white/60"
            >
              ← Dashboard
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-black text-green-700">
                  Contact Queries
                </h1>
                {unreadCount > 0 && (
                  <span
                    className="px-2.5 py-0.5 rounded-full text-xs font-extrabold text-white"
                    style={{
                      background: "linear-gradient(135deg,#ef4444,#dc2626)",
                    }}
                  >
                    {unreadCount} new
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm">
                Messages from customers via the Contact page
              </p>
            </div>
          </div>

          {/* stats chips */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: "Total", value: total, color: "#2196F3" },
              { label: "New", value: unreadCount, color: "#ef4444" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl px-4 py-2 text-center"
                style={{
                  background: `${s.color}12`,
                  border: `1px solid ${s.color}25`,
                }}
              >
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  {s.label}
                </p>
                <p className="text-xl font-black" style={{ color: s.color }}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Filter tabs ── */}
        <div className="glass-card p-3 mb-5 flex gap-2 flex-wrap">
          {["all", "unread", "read", "resolved"].map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className="px-4 py-2 rounded-xl text-xs font-bold transition-all"
              style={{
                background:
                  filter === f
                    ? "linear-gradient(135deg,#2D8B4E,#4CAF72)"
                    : "rgba(45,139,78,0.06)",
                color: filter === f ? "#fff" : "#2D8B4E",
                border:
                  filter === f ? "none" : "1.5px solid rgba(45,139,78,0.15)",
              }}
            >
              {f === "all"
                ? `All (${total})`
                : `${STATUS_META[f]?.label}${f === "unread" ? ` (${unreadCount})` : ""}`}
            </button>
          ))}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
          </div>
        )}

        {/* ── Empty ── */}
        {!loading && queries.length === 0 && (
          <div className="glass-card p-12 text-center">
            <div className="text-5xl mb-3">📭</div>
            <p className="font-bold text-gray-600 text-lg">No queries yet</p>
            <p className="text-gray-400 text-sm mt-1">
              {filter !== "all"
                ? "Try changing the filter above"
                : "When customers submit the contact form, they&apos;ll appear here"}
            </p>
          </div>
        )}

        {/* ── Queries ── */}
        {!loading && queries.length > 0 && (
          <div className="flex flex-col gap-3">
            {/* New / unread first with highlight */}
            {[...unreadQueries, ...otherQueries].map((q) => {
              const sm = STATUS_META[q.status] || STATUS_META.read;
              const isExp = expanded === q._id;
              const isNew = q.status === "unread";

              return (
                <div
                  key={q._id}
                  className="glass-card overflow-hidden transition-all"
                  style={{
                    border: isNew
                      ? "1.5px solid #fca5a5"
                      : "1px solid rgba(0,0,0,0.05)",
                    boxShadow: isNew
                      ? "0 2px 16px rgba(239,68,68,0.08)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* ── Card header ── */}
                  <div
                    className="flex items-start justify-between p-4 gap-3 cursor-pointer"
                    onClick={() => {
                      setExpanded(isExp ? null : q._id);
                      if (q.status === "unread") updateStatus(q._id, "read");
                    }}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* avatar */}
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                        style={{
                          background: isNew
                            ? "linear-gradient(135deg,#ef4444,#f87171)"
                            : "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                        }}
                      >
                        {q.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-800 text-sm">
                            {q.name}
                          </span>
                          {isNew && (
                            <span
                              className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                              style={{
                                background:
                                  "linear-gradient(135deg,#ef4444,#dc2626)",
                              }}
                            >
                              NEW
                            </span>
                          )}
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{
                              background: sm.bg,
                              color: sm.color,
                              border: `1px solid ${sm.border}`,
                            }}
                          >
                            {sm.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="text-xs text-gray-500">
                            {SUBJECT_EMOJI[q.subject] || "📋"} {q.subject}
                          </span>
                          <span className="text-xs text-gray-400">·</span>
                          <span className="text-xs text-gray-400">
                            {fmtDate(q.createdAt)}
                          </span>
                        </div>
                        {/* message preview */}
                        {!isExp && (
                          <p
                            className="text-xs text-gray-500 mt-1.5 leading-relaxed"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {q.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* contact info + chevron */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        {q.email && (
                          <p className="text-xs font-semibold text-gray-600">
                            {q.email}
                          </p>
                        )}
                        {q.phone && (
                          <p className="text-xs text-gray-400">{q.phone}</p>
                        )}
                      </div>
                      <span
                        className="text-gray-400 text-sm transition-transform"
                        style={{
                          transform: isExp ? "rotate(180deg)" : "none",
                          display: "inline-block",
                        }}
                      >
                        ▾
                      </span>
                    </div>
                  </div>

                  {/* ── Expanded detail ── */}
                  {isExp && (
                    <div className="px-4 pb-4">
                      <div
                        className="rounded-xl p-4 mb-4"
                        style={{
                          background: "rgba(45,139,78,0.04)",
                          border: "1px solid rgba(45,139,78,0.1)",
                        }}
                      >
                        <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">
                          Message
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {q.message}
                        </p>
                      </div>

                      {/* contact details on mobile */}
                      <div className="flex flex-wrap gap-3 mb-4 sm:hidden">
                        {q.email && (
                          <a
                            href={`mailto:${q.email}`}
                            className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-full border border-green-100"
                          >
                            ✉️ {q.email}
                          </a>
                        )}
                        {q.phone && (
                          <a
                            href={`tel:${q.phone}`}
                            className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-full border border-green-100"
                          >
                            📞 {q.phone}
                          </a>
                        )}
                      </div>

                      {/* reply + status actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {q.email && (
                          <a
                            href={`mailto:${q.email}?subject=Re: ${encodeURIComponent(q.subject)}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white"
                            style={{
                              background:
                                "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                            }}
                          >
                            ✉️ Reply via Email
                          </a>
                        )}
                        {q.phone && (
                          <a
                            href={`tel:${q.phone}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border border-green-200 text-green-700 bg-green-50"
                          >
                            📞 Call
                          </a>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          <select
                            value={q.status}
                            disabled={updating === q._id}
                            onChange={(e) =>
                              updateStatus(q._id, e.target.value)
                            }
                            className="text-xs font-bold rounded-lg px-2 py-1.5 border cursor-pointer outline-none"
                            style={{
                              background: sm.bg,
                              color: sm.color,
                              borderColor: sm.border,
                              opacity: updating === q._id ? 0.6 : 1,
                            }}
                          >
                            <option value="unread">New</option>
                            <option value="read">Read</option>
                            <option value="resolved">Resolved</option>
                          </select>
                          <button
                            onClick={() => deleteQuery(q._id)}
                            disabled={deleting === q._id}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                            title="Delete query"
                          >
                            {deleting === q._id ? "…" : "🗑"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-xl text-sm font-bold border disabled:opacity-40 transition-all hover:bg-green-50 border-green-200 text-green-700"
            >
              ← Prev
            </button>
            <span className="text-sm font-semibold text-gray-500 px-2">
              Page {page} of {pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="px-4 py-2 rounded-xl text-sm font-bold border disabled:opacity-40 transition-all hover:bg-green-50 border-green-200 text-green-700"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
